// Every tour, tip, and checklist step shown by the in-app help. Targets are CSS
// selectors for elements the pages already render; a step or tip whose target
// is not on screen is skipped (tips wait until it appears).
//
// Tour step: { target?, title, body }. No target = a centered card.
// Tip: { id, route?, target, title, body }. No route = any page.

const RAIL = (label) => `.nav-rail-btn[aria-label="${label}"]`

const ADD_WINDOW = {
  target: RAIL('Add window'),
  title: 'Open more windows',
  body: 'Add window opens another window beside this one, so you can compare two things side by side. Click a window to make it the active one; what you pick in the sidebar opens there.',
}

export const WELCOME_TOUR = {
  id: 'tour:welcome',
  steps: [
    {
      title: 'Welcome to Codetalk',
      body: 'Codetalk is a workspace for building codes and standards: read them, ask AI questions about them, take notes, and discuss them with your team. This quick tour shows you around, and short tips will appear the first time you meet each feature.',
    },
    {
      target: '.app-navtabs',
      title: 'Your workspaces',
      body: 'Each tab is a different workspace. Code is the library of standards. Searchrooms and Global AI Search answer questions. Global Text Search finds exact wording. Notes, Guides, Topics, Chat, and Network help you organize and share.',
    },
    {
      target: '.help-menu-btn',
      title: 'Help is always here',
      body: 'Open this menu to replay a page tour, reread tips, see your Getting started checklist, or turn automatic help off.',
    },
  ],
}

export const PAGE_TOURS = {
  '/code': {
    id: 'tour:/code',
    steps: [
      {
        target: '.code-nav',
        title: 'The code library',
        body: 'Every standard in the library is listed here. Click a section name to open it in the PDF viewer. Use the arrow on a row to step inside a standard and see its chapters and sections.',
      },
      {
        target: '.code-viewtoggle',
        title: 'Two ways to browse',
        body: 'Switch between stepping into one level at a time and seeing the whole outline expanded.',
      },
      {
        target: '.nav-filter-lead',
        title: 'Narrow the library',
        body: 'Open the filters to search section names, and filter by topics you have filed things under.',
      },
      {
        target: '.nav-rail',
        title: 'Quick actions',
        body: 'This strip holds the actions for the page: open another window, bookmark a section, and create or manage topics. The top button collapses the sidebar.',
      },
      {
        target: '.main-display',
        title: 'Windows',
        body: 'The section you open appears here with its PDF, plus comments and guides beside it.',
      },
      ADD_WINDOW,
    ],
  },
  '/searchrooms': {
    id: 'tour:/searchrooms',
    steps: [
      {
        target: 'aside.main-nav',
        title: 'Searchrooms',
        body: 'A searchroom is an AI chat that only answers from the code sections you choose. That keeps answers focused on the standards that matter to your project. Your searchrooms are listed here.',
      },
      {
        target: RAIL('New searchroom'),
        title: 'Create a searchroom',
        body: 'Name it and pick the standards or sections it should search.',
      },
      {
        target: RAIL('Search searchrooms'),
        title: 'Find past answers',
        body: 'Search the messages in all your searchrooms. Shortcut: Ctrl+K (Cmd+K on a Mac).',
      },
      {
        target: '.main-display',
        title: 'Chat and PDF side by side',
        body: 'Ask questions on the left. Answers cite the sections they used; click a citation to open that page on the right.',
      },
      ADD_WINDOW,
    ],
  },
  '/global-ai-search': {
    id: 'tour:/global-ai-search',
    steps: [
      {
        target: 'aside.main-nav',
        title: 'Global AI Search',
        body: 'Ask a question across the entire library when you do not know which standard has the answer. Each conversation is saved here.',
      },
      {
        target: RAIL('New chat'),
        title: 'Start a conversation',
        body: 'Create a new chat, then ask your question in plain words.',
      },
      {
        target: '.main-display',
        title: 'Answers with sources',
        body: 'Answers list the sections they drew from. Click one to read the page in the viewer.',
      },
    ],
  },
  '/global-text-search': {
    id: 'tour:/global-text-search',
    steps: [
      {
        target: 'aside.main-nav',
        title: 'Global Text Search',
        body: 'Find exact wording across every standard, without AI. Use it when you know the words you are looking for. Saved searches are listed here.',
      },
      {
        target: RAIL('New search'),
        title: 'Create a search',
        body: 'Give it a name, then add the words to find and the words to leave out.',
      },
      {
        target: '.main-display',
        title: 'Results open the page',
        body: 'Run the search, then click any result to open that page with the matches highlighted.',
      },
    ],
  },
  '/notebooks': {
    id: 'tour:/notebooks',
    steps: [
      {
        target: 'aside.main-nav',
        title: 'Notes',
        body: 'Your notes live here. A note can hold pages inside it, so you can organize by project, exam, or topic.',
      },
      {
        target: RAIL('New note'),
        title: 'Start a note',
        body: 'Create a note, give it a title, and start writing.',
      },
      {
        target: '.main-display',
        title: 'Write beside the code',
        body: 'Notes can hold text, headings, code sections, guides, images, PDFs, and saved AI answers. Click a code section in a note to open it in the viewer next to it.',
      },
    ],
  },
  '/guides': {
    id: 'tour:/guides',
    steps: [
      {
        target: '.guides-nav',
        title: 'Guides',
        body: 'Guides are your reference material: links, PDFs, videos, images, and files. Attach them to code sections, notes, and chat messages so the right help is always one click away.',
      },
      {
        target: RAIL('New guide'),
        title: 'Add a guide',
        body: 'Upload a file or paste a link.',
      },
      {
        target: '.main-display',
        title: 'Open guides here',
        body: 'Click a guide in the list to view it here.',
      },
    ],
  },
  '/topics': {
    id: 'tour:/topics',
    steps: [
      {
        target: 'aside.main-nav',
        title: 'Topics',
        body: 'Topics are folders that work across the whole app. File bookmarks, notes, searchrooms, guides, comments, and chats under a topic, then find them all in one place. Topics can sit inside other topics.',
      },
      {
        target: RAIL('New topic'),
        title: 'Create a topic',
        body: 'New topics are created inside the level you are viewing.',
      },
      {
        target: '.main-display',
        title: 'Everything filed under a topic',
        body: 'Pick a topic to see what is filed under it, and choose what kind of things to list.',
      },
    ],
  },
  '/chat': {
    id: 'tour:/chat',
    steps: [
      {
        target: 'aside.main-nav',
        title: 'Chat',
        body: 'Private conversations with people you work with. Your chats are listed here.',
      },
      {
        target: RAIL('New chat'),
        title: 'Start a chat',
        body: 'Pick the people to include. You can name the chat and add more people later.',
      },
      {
        target: '.main-display',
        title: 'Share code, not just words',
        body: 'Attach code sections and guides to a message so everyone opens the same page. Reply in threads and like messages.',
      },
    ],
  },
  '/network': {
    id: 'tour:/network',
    steps: [
      {
        target: '.net-tools',
        title: 'Your network',
        body: 'Find people, see your connections, and answer connection requests.',
      },
      {
        target: RAIL('My profile'),
        title: 'Your profile',
        body: 'Open your profile to see how others see you and to edit it.',
      },
    ],
  },
}

export const TIPS = [
  // Code
  {
    id: 'tip:pdf-toolbar',
    target: '.pdfviewer-toolbar',
    title: 'PDF tools',
    body: 'Move between pages, switch to a grid of page thumbnails to survey the document, search inside the PDF, mark up the page, rotate, and zoom.',
  },
  {
    id: 'tip:markup',
    target: '.markup-toolbar',
    title: 'Mark up the page',
    body: 'Highlight, add text boxes, draw lines, arrows, and shapes right on the page. Your markups stay on the standard for next time.',
  },
  {
    id: 'tip:code-notes',
    route: '/code',
    target: 'select.code-notes-toggle',
    title: 'Comments and guides',
    body: 'Every section has its own discussion. Switch here between comments from people and the guides attached to this section.',
  },
  {
    id: 'tip:page-marks',
    route: '/code',
    target: '.page-mark',
    title: 'C and G markers',
    body: 'A C or G on a page means it has comments or guides. Click the marker to open them.',
  },
  {
    id: 'tip:bookmark',
    route: '/code',
    target: '.code-nav-bookmark-btn',
    title: 'Bookmark sections',
    body: 'Bookmark a section to find it again quickly. File bookmarks under topics to group them.',
  },
  {
    id: 'tip:dock',
    target: '.grid-dock-trigger',
    title: 'Minimized windows',
    body: 'Windows you minimize wait here. Click to bring one back.',
  },
  // AI answers
  {
    id: 'tip:answer-refs',
    target: '.msg-band-assistant .ref-chips',
    title: 'Cited sections',
    body: 'These are the sections the answer used. Click one to open the exact page and check the wording yourself.',
  },
  {
    id: 'tip:answer-actions',
    target: '.msg-actions',
    title: 'Keep a good answer',
    body: 'Download the answer, add it to one of your notes, or send it to a chat. It keeps its formatting and citations.',
  },
  {
    id: 'tip:pdf-item-select',
    target: '.pdf-item-select',
    title: 'Switch sections',
    body: 'Choose which of the searchroom’s sections to show in the viewer.',
  },
  // Text search
  {
    id: 'tip:text-rules',
    route: '/global-text-search',
    target: '.ts-builder-actions',
    title: 'Build the search',
    body: 'Add words to find and words to leave out, then press Run.',
  },
  {
    id: 'tip:text-results',
    route: '/global-text-search',
    target: '.ts-results-scroll',
    title: 'Open a match',
    body: 'Click a result to open that page with the matching words highlighted.',
  },
  // Notes
  {
    id: 'tip:note-blocks',
    route: '/notebooks',
    target: '.nb-typebar',
    title: 'Add blocks',
    body: 'Add text, headings, a sketch, code sections, guides, topics, images, PDFs, or a page inside this one. Shortcut: type / at the start of any line.',
  },
  {
    id: 'tip:note-grip',
    route: '/notebooks',
    target: '.nb-block-grip',
    title: 'Reorder blocks',
    body: 'Hover a block and drag the dotted handle to move it.',
  },
  {
    id: 'tip:note-answer',
    route: '/notebooks',
    target: '.nb-answer',
    title: 'Saved AI answer',
    body: 'This is a copy of an AI answer with its question. Click a cited section to open it beside your note.',
  },
  // Topics
  {
    id: 'tip:topic-kind',
    route: '/topics',
    target: '.topic-kind-select',
    title: 'Choose what to list',
    body: 'Show the bookmarks, notes, searchrooms, guides, or other things filed under this topic.',
  },
  {
    id: 'tip:topic-nested',
    route: '/topics',
    target: '.topic-kind-nested',
    title: 'Include nested topics',
    body: 'Turn this on to also list things filed under the topics inside this one.',
  },
  // Chat
  {
    id: 'tip:chat-attach',
    route: '/chat',
    target: '.crm-composer',
    title: 'Attach code and guides',
    body: 'Use Attach to add code sections or guides to your message. Everyone in the chat can open them.',
  },
]

// Getting started, in the order a new user would naturally do them.
export const CHECKLIST = [
  {
    id: 'open-section',
    title: 'Open a code section',
    body: 'In Code, pick a standard and click a section to read it in the PDF viewer.',
    to: '/code',
  },
  {
    id: 'bookmark',
    title: 'Bookmark a section',
    body: 'Use the bookmark button on a section in the Code library.',
    to: '/code',
  },
  {
    id: 'comment',
    title: 'Comment on a section',
    body: 'Open a section and post in its comments.',
    to: '/code',
  },
  {
    id: 'searchroom',
    title: 'Create a searchroom',
    body: 'Choose the sections an AI chat should answer from.',
    to: '/searchrooms',
  },
  {
    id: 'ask',
    title: 'Ask the AI a question',
    body: 'Ask in a searchroom, or across the whole library in Global AI Search.',
    to: '/searchrooms',
  },
  {
    id: 'save-answer',
    title: 'Save an AI answer',
    body: 'Under an answer, choose Add to note or Send to chat.',
    to: '/searchrooms',
  },
  {
    id: 'note',
    title: 'Start a note',
    body: 'Create a note and add a code section to it.',
    to: '/notebooks',
  },
  {
    id: 'chat',
    title: 'Send a chat message',
    body: 'Start a chat with a colleague and share a section.',
    to: '/chat',
  },
]

// Steps with no database record, ticked when their element is on screen.
export const OBSERVED_STEPS = [
  { step: 'open-section', route: '/code', target: '.code-viewer-stage .pdfviewer-page' },
]

// What each page is for, behind the info dot beside the page name.
export const PAGE_INFO = {
  '/code': 'The library of building codes and standards. Open any section as a PDF, bookmark it, comment on it, and attach guides.',
  '/searchrooms': 'AI chats that only answer from the code sections you choose, with citations you can open.',
  '/global-ai-search': 'Ask AI questions across the entire library when you do not know which standard has the answer.',
  '/global-text-search': 'Find exact wording across every standard, without AI.',
  '/notebooks': 'Your notes. Mix your own writing with code sections, guides, images, PDFs, and saved AI answers.',
  '/guides': 'Your reference material: links, PDFs, videos, images, and files to attach anywhere in the app.',
  '/topics': 'Folders that work across the app. Everything filed under a topic, in one place.',
  '/chat': 'Private conversations. Attach code sections and guides so everyone opens the same page.',
  '/network': 'Find people, manage connections, and edit your profile.',
}

// Video walkthroughs by page. Add a URL here and a "Watch a video" link shows
// in the help menu on that page.
export const VIDEOS = {}
